/**
 * useFocusTrap — M8 from the 2026-05-27 audit.
 *
 * Confines keyboard focus inside a modal element while it's open:
 *   - Moves focus to the first focusable child on mount.
 *   - Cycles focus between first ↔ last on Tab / Shift+Tab.
 *   - Calls `onEscape` (typically the dialog's close handler) on Escape.
 *   - Restores focus to whatever was active before the dialog opened, on
 *     unmount. (Important for screen-reader / keyboard users so they don't
 *     get dumped at the top of the page after dismissing the dialog.)
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useFocusTrap(ref, onClose);
 *   return <div ref={ref} role="dialog" aria-modal="true"> ... </div>;
 */
import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function useFocusTrap(
  ref: RefObject<HTMLElement>,
  onEscape: () => void,
): void {
  useEffect(() => {
    const container = ref.current;
    if (!container) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus inside the dialog on open. Use the first focusable element
    // when available; otherwise focus the container itself so Escape still
    // works.
    const firstPass = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const firstFocusable = firstPass[0];
    if (firstFocusable) firstFocusable.focus();
    else container.setAttribute('tabindex', '-1'), container.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      // Re-query each keystroke — the focusable set can change while the
      // dialog is open (loading spinners, conditional fields).
      const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (!first || !last) { e.preventDefault(); return; }
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !container.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocused?.focus?.();
    };
  }, [ref, onEscape]);
}
